"use client"
import { useNotifications } from '@/contexts/NotificationContext';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  AlertCircle,
  ArrowRight,
  Bell,
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  FileText,
  Tag,
  User,
  X
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';

export default function NotificationDropdown() {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Call hook unconditionally (React hooks rule)
  const {
    notifications = [],
    unreadCount = 0,
    markAsRead,
    deleteNotification,
    hasNewNotification = false,
    clearNewNotificationFlag,
    loading = false,
  } = useNotifications();

  // Get the 5 most recent notifications
  const recentNotifications = notifications?.slice(0, 5) || [];

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'blog-scheduled':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'blog-published':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'blog-draft':
        return <FileText className="h-4 w-4 text-orange-600" />;
      case 'system':
        return <AlertCircle className="h-4 w-4 text-purple-600" />;
      case 'user-activity':
        return <User className="h-4 w-4 text-gray-600" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const formatTimestamp = (timestamp: string | Date) => {
    if (!mounted) return new Date(timestamp).toLocaleDateString();
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleNotificationClick = async (notificationId: string, isRead: boolean) => {
    if (!isRead && notificationId) {
      try {
        await markAsRead(notificationId);
      } catch (err) {
        console.error('Error marking notification as read:', err);
        setError('Failed to mark notification as read');
      }
    }
  };

  const handleDelete = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    if (!notificationId) return;
    try {
      await deleteNotification(notificationId);
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  };

  const handleViewMore = () => {
    setIsOpen(false);
    router.push('/admin/notifications');
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      clearNewNotificationFlag();
      setError(null); // Clear any errors when opening
    }
  };

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={`relative p-1.5 rounded-lg bg-blue-50/50 backdrop-blur-md border border-blue-100/50 hover:bg-blue-100/60 hover:border-blue-200/60 transition-all duration-200 cursor-pointer ${hasNewNotification ? 'animate-bell-shake' : ''
            }`}
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400 hover:text-blue-500 transition-colors" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-4 w-4 sm:h-5 sm:w-5 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full z-10">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="w-[380px] max-w-[95vw] bg-white rounded-lg shadow-lg border border-gray-200 max-h-[80vh] overflow-hidden z-[9999]"
          sideOffset={5}
          align="end"
          side="bottom"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-xs text-gray-500">
                  {unreadCount} unread
                </span>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[60vh]">
            {error && (
              <div className="px-4 py-2 bg-red-50 border-b border-red-200">
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}
            {loading ? (
              <div className="px-4 py-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-sm text-gray-500">Loading notifications...</p>
              </div>
            ) : recentNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recentNotifications.map((notification) => (
                  <div
                    key={notification._id}
                    className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${!notification.isRead ? 'bg-blue-50' : ''
                      }`}
                    onClick={() => handleNotificationClick(notification._id!, notification.isRead)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className={`text-sm font-medium ${!notification.isRead ? 'text-blue-900' : 'text-gray-900'
                              }`}>
                              {notification.title}
                            </h4>
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {notification.message}
                            </p>

                            {/* Metadata */}
                            {notification.metadata && (
                              <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500">
                                {notification.metadata.author && (
                                  <div className="flex items-center space-x-1">
                                    <User className="h-3 w-3" />
                                    <span>{notification.metadata.author}</span>
                                  </div>
                                )}
                                {notification.metadata.scheduledDate && (
                                  <div className="flex items-center space-x-1">
                                    <Calendar className="h-3 w-3" />
                                    <span>{new Date(notification.metadata.scheduledDate).toLocaleDateString()}</span>
                                  </div>
                                )}
                                {notification.metadata.tags && notification.metadata.tags.length > 0 && (
                                  <div className="flex items-center space-x-1">
                                    <Tag className="h-3 w-3" />
                                    <span>{notification.metadata.tags[0]}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="flex items-center space-x-2 mt-2">
                              <span className="text-xs text-gray-500">
                                {formatTimestamp(notification.timestamp)}
                              </span>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${notification.priority === 'high' ? 'bg-red-100 text-red-800' :
                                notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                {notification.priority}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center space-x-1 ml-2">
                            {!notification.isRead && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification._id!);
                                }}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Mark as read"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDelete(e, notification._id!)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {recentNotifications.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleViewMore}
                className="w-full flex items-center justify-center space-x-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                <span>View All Notifications</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>

      <style jsx global>{`
        @keyframes bell-shake {
          0%, 100% { transform: rotate(0deg); }
          10%, 30%, 50%, 70%, 90% { transform: rotate(-10deg); }
          20%, 40%, 60%, 80% { transform: rotate(10deg); }
        }

        .animate-bell-shake {
          animation: bell-shake 0.8s ease-in-out;
        }
      `}</style>
    </DropdownMenu.Root>
  );
}

